import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { api, errMsg } from "../../lib/api";
import { useApp } from "../../lib/ctx";

const refCache = {};
export function RefSelect({ entity, value, onChange, labelKey = "name", filter = "", testid, allowEmpty = true }) {
  const [opts, setOpts] = useState(refCache[entity + filter] || []);
  useEffect(() => {
    api.get(`/e/${entity}${filter ? "?" + filter : ""}`).then((r) => { refCache[entity + filter] = r.data; setOpts(r.data); }).catch(() => {});
  }, [entity, filter]);
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value, opts.find((o) => o.id === e.target.value))}
      className="h-10 w-full rounded-xl border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/40">
      {allowEmpty && <option value="">Select...</option>}
      {opts.map((o) => <option key={o.id} value={o.id}>{[o.number, o[labelKey] || o.title || o.name].filter(Boolean).join(" · ")}</option>)}
    </select>
  );
}

export function UserSelect({ value, onChange, testid }) {
  const { users } = useApp();
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value, users.find((u) => u.user_id === e.target.value))}
      className="h-10 w-full rounded-xl border border-input bg-white px-3 text-sm">
      <option value="">Select person...</option>
      {users.filter((u) => u.role !== "pending").map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
    </select>
  );
}

export function FieldInput({ f, value, onChange, data }) {
  const tid = `field-${f.key}`;
  const cls = "h-10 rounded-xl bg-white";
  if (f.render) return f.render({ value, onChange, data });
  if (f.type === "textarea") return <Textarea data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="rounded-xl" />;
  if (f.type === "select") return (
    <select data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-xl border border-input bg-white px-3 text-sm">
      <option value="">Select...</option>
      {f.options.map((o) => <option key={o} value={o}>{String(o).replace(/_/g, " ")}</option>)}
    </select>
  );
  if (f.type === "ref") return <RefSelect testid={tid} entity={f.entity} labelKey={f.labelKey} filter={f.filter} value={value}
    onChange={(v, o) => onChange(v, o)} />;
  if (f.type === "user") return <UserSelect testid={tid} value={value} onChange={onChange} />;
  if (f.type === "checkbox") return <input data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#FF5A00]" />;
  return <Input data-testid={tid} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="any" value={value ?? ""}
    onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className={cls} />;
}

export default function RecordForm({ open, onClose, entity, fields, initial = {}, record, title, onSaved, endpoint }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState(null);
  useEffect(() => { if (open) { setData(record ? { ...record } : { ...initial }); setDups(null); } }, [open]); // eslint-disable-line
  const set = (k, v, o, f) => setData((d) => ({ ...d, [k]: v, ...(f?.onPick && o ? f.onPick(o) : {}) }));
  const save = async (allowDup = false) => {
    for (const f of fields) if (f.required && (data[f.key] === undefined || data[f.key] === "")) return toast.error(`${f.label} is required`);
    setSaving(true);
    try {
      const body = {};
      fields.forEach((f) => { if (data[f.key] !== undefined) body[f.key] = data[f.key]; });
      Object.keys(initial).forEach((k) => { if (body[k] === undefined) body[k] = initial[k]; });
      if (allowDup) body.allow_duplicate = true;
      const r = record ? await api.patch(`/e/${entity}/${record.id}`, body) : await api.post(endpoint || `/e/${entity}`, body);
      toast.success(record ? "Saved" : "Created");
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.detail?.duplicates) setDups(e.response.data.detail.duplicates);
      else toast.error(errMsg(e));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="record-form">
        <DialogHeader><DialogTitle>{title || (record ? "Edit" : "New")}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          {fields.filter((f) => !f.hidden).map((f) => (
            <div key={f.key} className={f.type === "textarea" || f.wide ? "sm:col-span-2" : ""}>
              <label className="text-xs font-medium text-neutral-600 mb-1.5 block">{f.label}{f.required && <span className="text-[#FF2D00]"> *</span>}</label>
              <FieldInput f={f} value={data[f.key]} data={data} onChange={(v, o) => set(f.key, v, o, f)} />
              {f.help && <p className="text-[11px] text-neutral-400 mt-1">{f.help}</p>}
            </div>
          ))}
        </div>
        {dups && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="duplicate-warning">
            <p className="font-medium text-amber-800">Possible duplicate found</p>
            {dups.map((d) => <p key={d.id} className="text-amber-700">{d.entity}: {d.name} · {d.phone} {d.email}</p>)}
            <button data-testid="create-anyway-btn" className="btn-ghost-o mt-2" onClick={() => save(true)}>Create anyway</button>
          </div>
        )}
        <DialogFooter>
          <button className="btn-outline-o" onClick={onClose} data-testid="form-cancel-btn">Cancel</button>
          <button className="btn-grad" disabled={saving} onClick={() => save(false)} data-testid="form-save-btn">{saving ? "Saving..." : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
